import { useEffect, useMemo, useState } from 'react';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';

const EMPTY_CONTACTO = {
  id: null,
  nombre: '',
  rolSector: '',
  email: '',
  telefono: '',
  celular: '',
  principal: false,
  operaciones: false,
  cobranzas: false,
  canalPreferido: '',
  horarioDesde: '',
  horarioHasta: '',
  activo: true,
};

const CANALES = ['Email', 'WhatsApp', 'Teléfono', 'Portal'];

export default function ContactoTab({ data = [], onChange }) {
  const [contactos, setContactos] = useState(data || []);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState(EMPTY_CONTACTO);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setContactos(data || []);
  }, [data]);

  const visibleContactos = useMemo(
    () => contactos.filter(contacto => contacto.activo !== false),
    [contactos]
  );

  const resetForm = () => {
    setFormState(EMPTY_CONTACTO);
    setEditingIndex(null);
    setErrors({});
  };

  const openNew = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (index) => {
    setFormState({ ...contactos[index] });
    setEditingIndex(index);
    setErrors({});
    setModalOpen(true);
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formState.nombre.trim()) {
      nextErrors.nombre = 'El nombre es obligatorio.';
    }
    if (formState.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      nextErrors.email = 'Email inválido.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    let next = [...contactos];
    const updated = {
      ...formState,
      nombre: formState.nombre.trim(),
      rolSector: formState.rolSector?.trim() || '',
      email: formState.email?.trim() || '',
      telefono: formState.telefono?.trim() || '',
      celular: formState.celular?.trim() || '',
      canalPreferido: formState.canalPreferido?.trim() || '',
      horarioDesde: formState.horarioDesde?.trim() || '',
      horarioHasta: formState.horarioHasta?.trim() || '',
      activo: formState.activo !== false,
    };

    if (updated.principal) {
      next = next.map(item => ({ ...item, principal: false }));
    }

    if (editingIndex != null) {
      next[editingIndex] = { ...next[editingIndex], ...updated };
    } else {
      next = [...next, updated];
    }

    setContactos(next);
    onChange?.(next);
    setModalOpen(false);
    resetForm();
  };

  const handleDelete = (index) => {
    const next = contactos.map((item, idx) =>
      idx === index ? { ...item, activo: false } : item
    );
    setContactos(next);
    onChange?.(next);
  };

  const handleChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[rgb(var(--fg))]">Contactos del cliente</h3>
          <p className="text-sm text-[rgba(var(--muted),0.85)]">
            Registrá múltiples contactos con roles y preferencias de comunicación.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))]"
          onClick={openNew}
        >
          Agregar contacto
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))]">
        <table className="min-w-full text-sm">
          <thead className="border-b border-[rgba(var(--border),0.4)] text-left text-[rgba(var(--muted),0.9)]">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Rol/Sector</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Teléfono</th>
              <th className="px-4 py-3">Celular</th>
              <th className="px-4 py-3">Flags</th>
              <th className="px-4 py-3">Canal</th>
              <th className="px-4 py-3">Horario</th>
              <th className="px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleContactos.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-[rgba(var(--muted),0.8)]" colSpan={9}>
                  No hay contactos cargados.
                </td>
              </tr>
            ) : (
              visibleContactos.map((contacto, index) => (
                <tr key={`${contacto.nombre}-${index}`} className="border-b border-[rgba(var(--border),0.2)]">
                  <td className="px-4 py-3 font-medium text-[rgb(var(--fg))]">{contacto.nombre}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{contacto.rolSector || '-'}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{contacto.email || '-'}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{contacto.telefono || '-'}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{contacto.celular || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {contacto.principal ? (
                        <span className="rounded-full bg-[rgba(var(--brand),0.15)] px-2 py-0.5 text-xs text-[rgb(var(--brand))]">
                          Principal
                        </span>
                      ) : null}
                      {contacto.operaciones ? (
                        <span className="rounded-full bg-[rgba(15,118,110,0.15)] px-2 py-0.5 text-xs text-[rgb(15,118,110)]">
                          Operaciones
                        </span>
                      ) : null}
                      {contacto.cobranzas ? (
                        <span className="rounded-full bg-[rgba(217,119,6,0.15)] px-2 py-0.5 text-xs text-[rgb(217,119,6)]">
                          Cobranzas
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{contacto.canalPreferido || '-'}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">
                    {contacto.horarioDesde || contacto.horarioHasta
                      ? `${contacto.horarioDesde || '--'} a ${contacto.horarioHasta || '--'}`
                      : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs text-[rgb(var(--brand))] hover:underline"
                        onClick={() => openEdit(index)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="text-xs text-[rgb(var(--muted))] hover:text-[rgb(var(--brand))]"
                        onClick={() => handleDelete(index)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ModalBase
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        labelledBy="modal-contactos-titulo"
        className="modal modal-content w-full max-w-2xl p-5 md:p-6 focus-visible:outline-none"
      >
        <div className="space-y-4">
          <div className="border-b border-[rgba(var(--border),0.6)] pb-3">
            <h4 id="modal-contactos-titulo" className="text-lg font-semibold text-[rgb(var(--fg))]">
              {editingIndex != null ? 'Editar contacto' : 'Nuevo contacto'}
            </h4>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Nombre *</label>
              <input
                value={formState.nombre}
                onChange={e => handleChange('nombre', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
              {errors.nombre ? <p className="text-xs text-[rgb(var(--brand))]">{errors.nombre}</p> : null}
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Rol / Sector</label>
              <input
                value={formState.rolSector}
                onChange={e => handleChange('rolSector', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Email</label>
              <input
                type="email"
                value={formState.email}
                onChange={e => handleChange('email', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
              {errors.email ? <p className="text-xs text-[rgb(var(--brand))]">{errors.email}</p> : null}
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Teléfono</label>
              <input
                value={formState.telefono}
                onChange={e => handleChange('telefono', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Celular</label>
              <input
                value={formState.celular}
                onChange={e => handleChange('celular', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Canal preferido</label>
              <select
                value={formState.canalPreferido}
                onChange={e => handleChange('canalPreferido', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              >
                <option value="">Seleccionar...</option>
                {CANALES.map(canal => (
                  <option key={canal} value={canal}>
                    {canal}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Horario desde</label>
              <input
                type="time"
                value={formState.horarioDesde}
                onChange={e => handleChange('horarioDesde', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Horario hasta</label>
              <input
                type="time"
                value={formState.horarioHasta}
                onChange={e => handleChange('horarioHasta', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input
                type="checkbox"
                checked={formState.principal}
                onChange={e => handleChange('principal', e.target.checked)}
                className="h-4 w-4 rounded border-[rgba(var(--border),0.6)]"
              />
              Principal
            </label>
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input
                type="checkbox"
                checked={formState.operaciones}
                onChange={e => handleChange('operaciones', e.target.checked)}
                className="h-4 w-4 rounded border-[rgba(var(--border),0.6)]"
              />
              Operaciones
            </label>
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input
                type="checkbox"
                checked={formState.cobranzas}
                onChange={e => handleChange('cobranzas', e.target.checked)}
                className="h-4 w-4 rounded border-[rgba(var(--border),0.6)]"
              />
              Cobranzas
            </label>
          </div>

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              className="border border-[rgba(var(--border),0.6)] bg-transparent"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="border border-[rgb(var(--brand))] bg-[rgb(var(--brand))] text-[rgb(var(--fg))]"
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </div>
      </ModalBase>
    </div>
  );
}
